/**
 * Sign In Screen Component
 *
 * Welcomes users and provides Google OAuth sign-in.
 * Warm, inviting design that emphasizes the celebration-focused nature of the app.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthActions } from '@convex-dev/auth/react';
import { Loader2, ArrowLeft, CheckCircle2 } from 'lucide-react';

interface SignInScreenProps {
  onBack?: () => void;
}

export function SignInScreen({ onBack }: SignInScreenProps) {
  const { signIn } = useAuthActions();
  const [loadingProvider, setLoadingProvider] = useState<'google' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleGoogleSignIn = async () => {
    setLoadingProvider('google');
    setError(null);

    try {
      await signIn('google');
    } catch (err) {
      console.error('Sign in failed:', err);
      setError('Sign in failed. Please try again.');
      setLoadingProvider(null);
    }
  };

  return (
    <div className="min-h-screen bg-bg-primary flex items-center justify-center p-4">
      {/* Back button */}
      {onBack && (
        <button
          onClick={onBack}
          className="fixed top-4 left-4 flex items-center gap-2 px-3 py-2 text-sm text-text-secondary
                   hover:text-text-primary rounded-lg hover:bg-bg-secondary transition-colors"
          type="button"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
      )}

      <div className="w-full max-w-md space-y-8">
        {/* Logo and tagline */}
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-white" />
            </div>
            <h1 className="font-display text-4xl text-primary">Have-Done</h1>
          </div>
          <p className="text-text-secondary text-lg">
            Celebrate what you've accomplished.
          </p>
        </div>

        {/* Main card */}
        <div className="rounded-xl bg-bg-secondary p-8 shadow-warm-md space-y-6">
          <div className="text-center space-y-2">
            <h2 className="text-xl font-semibold text-text-primary">Welcome</h2>
            <p className="text-text-secondary text-sm">
              Sign in to start logging your accomplishments
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div
              role="alert"
              className="p-3 rounded-lg bg-error/10 border border-error/30 text-sm text-error"
            >
              {error}
            </div>
          )}

          {/* Sign in buttons */}
          <div className="space-y-3">
            {/* Google sign in */}
            <button
              onClick={handleGoogleSignIn}
              disabled={loadingProvider !== null}
              className="w-full flex items-center justify-center gap-3 px-4 py-3
                         bg-white border border-border-light rounded-lg
                         hover:bg-bg-tertiary hover:shadow-warm-sm
                         transition-all duration-200 group
                         disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:shadow-none"
              type="button"
            >
              {loadingProvider === 'google' ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin text-text-tertiary" />
                  <span className="text-text-secondary font-medium">
                    Signing in...
                  </span>
                </>
              ) : (
                <>
                  {/* Google icon */}
                  <svg
                    className="w-5 h-5"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  <span className="text-text-primary font-medium">
                    Continue with Google
                  </span>
                </>
              )}
            </button>

          </div>

          {/* Decorative divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border-light" />
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div className="space-y-4">
          <div className="flex items-start gap-3 text-left">
            <div className="rounded-full bg-category-health/20 p-2 shrink-0">
              <svg
                className="w-4 h-4 text-category-health"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div>
              <p className="font-medium text-text-primary text-sm">
                Log accomplishments, not tasks
              </p>
              <p className="text-text-tertiary text-sm">
                Focus on what you've done, not what's left to do
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 text-left">
            <div className="rounded-full bg-category-creative/20 p-2 shrink-0">
              <svg
                className="w-4 h-4 text-category-creative"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
            <div>
              <p className="font-medium text-text-primary text-sm">
                Build positive momentum
              </p>
              <p className="text-text-tertiary text-sm">
                Track streaks and celebrate your wins
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 text-left">
            <div className="rounded-full bg-category-learning/20 p-2 shrink-0">
              <svg
                className="w-4 h-4 text-category-learning"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <div>
              <p className="font-medium text-text-primary text-sm">
                See your progress
              </p>
              <p className="text-text-tertiary text-sm">
                Weekly summaries show how much you've achieved
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-text-tertiary">
          By signing in, you agree to our{' '}
          <Link to="/terms" className="text-primary hover:underline">
            terms of service
          </Link>{' '}
          and{' '}
          <Link to="/privacy" className="text-primary hover:underline">
            privacy policy
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
